import { useState, useCallback, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ToggleChip } from '@/components/ui/toggle-chip'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { usersApi } from '@/lib/api'
import { track, AnalyticsEvents } from '@/lib/services/analytics'
import { useTheme } from '@/lib/theme-provider'
import { OnboardingStepProps } from '@/types/components/onboarding'
import { FadeUp } from '../FadeUp'

const MAX_SELECTIONS = 3

/**
 * Fixed JTBD (Jobs-to-be-Done) options for the onboarding wizard.
 * These drive both the stored preferences and the study carousel personalization.
 */
const JTBD_OPTIONS = [
  'Understand my consumers',
  'Build a stronger brand',
  'Test concepts & creative',
  'Find the right audience',
  'Explore markets & trends',
  'Improve products & experience',
] as const

/**
 * JtbdStep — onboarding step where users pick up to 3 research goals.
 *
 * Continue: persists `jtbd` to targeting preferences + fires analytics.
 * Skip: sets `selectedJtbd: []` in wizard state, no API call, no analytics.
 */
export const JtbdStep = forwardRef<HTMLDivElement, OnboardingStepProps>(
  function JtbdStep({ wizardState, onUpdateState, onNext, onSkip, className }, ref) {
    const { t } = useTranslation(['onboarding', 'common'])
    const { resolvedTheme } = useTheme()
    const tone = resolvedTheme === 'dark' ? 'dark' : 'light'
    const [isSaving, setIsSaving] = useState(false)

    const selectedJtbd = wizardState.selectedJtbd

    const toggleChip = useCallback(
      (value: string) => {
        const isSelected = selectedJtbd.includes(value)
        const next = isSelected
          ? selectedJtbd.filter((v) => v !== value)
          : selectedJtbd.length < MAX_SELECTIONS
            ? [...selectedJtbd, value]
            : selectedJtbd
        onUpdateState({ selectedJtbd: next })
      },
      [selectedJtbd, onUpdateState],
    )

    async function handleContinue() {
      setIsSaving(true)
      try {
        await usersApi.updateTargetingPreferences({
          targetingPreferences: { jtbd: selectedJtbd },
        })
        track(AnalyticsEvents.ONBOARDING_JTBD_SELECTED, {
          selected_count: selectedJtbd.length,
          values: selectedJtbd,
        })
        onNext()
      } catch {
        toast.warning(t('jtbd.saveWarning'))
        onNext()
      } finally {
        setIsSaving(false)
      }
    }

    function handleSkip() {
      // Skip sets an empty array so downstream logic (StudiesSection) falls
      // back to index 0. No API call, no analytics event.
      onUpdateState({ selectedJtbd: [] })
      onSkip()
    }

    return (
      <div ref={ref} tabIndex={-1} className={cn('flex flex-col gap-6 outline-none', className)}>
        {/* Heading */}
        <FadeUp delay={0} className="text-center">
          <Heading level={1} size="4xl">
            {t('jtbd.title')}
          </Heading>
          <Text size="sm" color="muted" className="mt-2">
            {t('jtbd.subtitle', { count: MAX_SELECTIONS })}
          </Text>
        </FadeUp>

        {/* JTBD chips */}
        <FadeUp delay={120}>
          <div
            role="group"
            aria-label={t('jtbd.aria')}
            className="flex flex-wrap justify-center gap-2"
            data-testid="jtbd-chip-grid"
          >
            {JTBD_OPTIONS.map((value, idx) => (
              <ToggleChip
                key={value}
                variant="platform"
                tone={tone}
                selected={selectedJtbd.includes(value)}
                showCheck
                data-testid={`jtbd-chip-${idx}`}
                onClick={() => toggleChip(value)}
                disabled={!selectedJtbd.includes(value) && selectedJtbd.length >= MAX_SELECTIONS}
              >
                {t(`jtbd.options.${value}`)}
              </ToggleChip>
            ))}
          </div>
        </FadeUp>

        {/* Actions */}
        <FadeUp delay={200} className="flex flex-col gap-2">
          <Button
            data-testid="jtbd-continue-button"
            variant="cta"
            onClick={handleContinue}
            disabled={isSaving}
            className="w-full"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('saving', { ns: 'common' })}
              </>
            ) : (
              t('continue', { ns: 'common' })
            )}
          </Button>
          <button
            type="button"
            data-testid="jtbd-skip-link"
            onClick={handleSkip}
            className="text-center text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            {t('changeLater', { ns: 'common' })}
          </button>
        </FadeUp>
      </div>
    )
  },
)
